import { Controller } from "@hotwired/stimulus"
import DropzoneModule from "dropzone"
const Dropzone = DropzoneModule.default || DropzoneModule
import { DirectUpload } from "@rails/activestorage"

const Circle = { r: 11, w: 2, R: 0, d: 0, c: 0 }
Circle.R = Circle.r + Circle.w
Circle.d = 2 * Circle.R
Circle.c = Math.PI * 2 * Circle.r

const template = `
<div class="dz-preview dz-file-preview">
  <img class="dz-image" data-dz-thumbnail />
  <div class="dz-ext">pdf</div>
  <video controls></video>
  <div class="dz-image-cover"></div>
  <svg class="dz-progress" width="${Circle.d}" fill="transparent" height="${Circle.d}" viewPort="0 0 ${Circle.d} ${Circle.d}" version="1.1" xmlns="http://www.w3.org/2000/svg">
    <circle class="dz-progress-bg" r="${Circle.r}" cx="${Circle.R}" cy="${Circle.R}" stroke="white" stroke-opacity="0.5" stroke-width="${Circle.w}" stroke-dasharray="${Circle.c}"></circle>
    <circle data-dz-uploadprogress class="dz-upload" r="${Circle.r}" cx="${Circle.R}" cy="${Circle.R}" stroke="white" stroke-width="${Circle.w}" stroke-dasharray="${Circle.c}" style="--c: ${Circle.c}px" stroke-linecap="round" transform="rotate(-90 ${Circle.R} ${Circle.R})"></circle>
  </svg>
  <div class="dz-details">
    <div class="dz-filename"><span data-dz-name translate="no"></span></div>
  </div>
</div>`

const DropzoneOptions = {
  thumbnailWidth: 112,
  thumbnailHeight: 112,
  autoQueue: false,
  uploadMultiple: true,
  addRemoveLinks: true,
  dictRemoveFile: '',
  previewTemplate: template,
}

class Upload {
  onprogress;
  file;
  constructor(file, url, callback, onprogress, onerror) {
    this.onprogress = onprogress
    this.file = file
    const upload = new DirectUpload(file, url, this)
    upload.create((error, blob) => {
      if (error) {
        // Surface the real error (e.g. a CORS-rejected S3 PUT reports "Status: 0") instead of a generic
        // message, so an upload that fails because the current host isn't allow-listed is diagnosable.
        console.error('Direct upload failed:', error)
        alert(`Upload failed: ${error.message || error}`)
        onerror()
      } else {
        callback(blob.signed_id)
        this.onprogress(100)
      }
    })
  }

  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress",
      event => this.directUploadDidProgress(event))
    request.upload.addEventListener("loadend",
      event => this.onprogress(99))
  }

  directUploadDidProgress(event) {
    const percent = event.loaded * 100 / event.total
    this.onprogress(Math.min(99, percent))
  }
}

export default class extends Controller {
  // file / filename / status: a single-file field's own display of its file (instead of Dropzone's preview tiles).
  static targets = ['fileField', 'dropzone', 'clickable', 'preview', 'submit', 'file', 'filename', 'status']

  static values = {
    acceptedFiles: { type: String, default: 'image/*, application/pdf, video/*' },
    rejectMessage: { type: String, default: 'Only image and PDF files can be attached.' },
    // 1 makes it a single-file field: a new file replaces the one already added.
    maxFiles: { type: Number, default: 0 }
  }

  numberOfFilesInProgress = 0

  onprogress(file, progress) {
    this.showProgress(file, progress)
    const progressBar = file.previewElement.querySelector("[data-dz-uploadprogress]")
    if (progressBar) {
      progressBar.style.strokeDashoffset = -progress / 100 * Circle.c
      if (progress === 100) {
        setTimeout(() => {
          file.previewElement.querySelector('.dz-progress').classList.add('opacity-0')
          file.previewElement.querySelector('.dz-image-cover')?.remove()
          this.numberOfFilesInProgress--
          this.updateSubmitButtonStatus()
        }, 500)
      }
    }
  }

  previewMedia(file) {
    const videoEl = file.previewElement.querySelector('video')
    if (file.type.startsWith('video/') && videoEl.canPlayType(file.type) !== 'no') {
      const fileURL = URL.createObjectURL(file)
      videoEl.setAttribute('src', fileURL)
      file.previewElement.classList.remove('dz-file-preview')
      file.previewElement.classList.add('dz-image-preview')
    } else if (file.type.startsWith('image/')) {
      videoEl.remove()
      file.previewElement.classList.remove('dz-file-preview')
      file.previewElement.classList.add('dz-image-preview')
    } else {
      videoEl.remove()
    }
  }

  // A single-file field shows its file in the drop area itself: its name, then how the upload is going. The element's
  // data-state (uploading / ready) styles it.
  showFile(file) {
    if (!this.hasFileTarget) return

    this.element.dataset.state = 'uploading'
    this.filenameTarget.textContent = `${file.name} (${this.formatSize(file.size)})`
    this.statusTarget.textContent = 'Uploading…'
  }

  showProgress(file, progress) {
    if (!this.hasFileTarget || !this.myDropzone?.files.includes(file)) return

    const ready = progress >= 100
    this.element.dataset.state = ready ? 'ready' : 'uploading'
    this.statusTarget.textContent = ready ? 'Uploaded' : `Uploading… ${Math.round(progress)}%`
  }

  formatSize(bytes) {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // A single-file field keeps only the newest file.
  replaceOthers(file) {
    if (this.maxFilesValue !== 1) return

    this.myDropzone.files.filter(other => other !== file).forEach(other => this.myDropzone.removeFile(other))
  }

  // The submit target, else the enclosing form's submit buttons — a form shouldn't submit mid-upload, before the
  // file's signed id is in it.
  submitButtons() {
    if (this.hasSubmitTarget) return [this.submitTarget]

    return Array.from(this.element.closest('form')?.querySelectorAll('[type=submit]') || [])
  }

  updateSubmitButtonStatus() {
    this.submitButtons().forEach(button => { button.disabled = this.numberOfFilesInProgress > 0 })
  }

  upload(file, input) {
    this.numberOfFilesInProgress++
    this.updateSubmitButtonStatus()
    const url = this.fileFieldTarget.dataset.directUploadUrl
    new Upload(file, url, (signed_id) => {
      // Removed (or replaced) while it was uploading: its blob must not ride along with the form.
      if (!this.myDropzone?.files.includes(file)) return

      const hiddenField = document.createElement('input')
      hiddenField.setAttribute("type", "hidden")
      hiddenField.setAttribute("value", signed_id)
      hiddenField.name = input.name
      if (input.dataset.errorKey) {
        hiddenField.dataset.errorKey = input.dataset.errorKey
      }
      this.element.appendChild(hiddenField)
      file.hiddenField = hiddenField
    }, progress => {
      this.onprogress(file, progress)
    }, () => this.uploadFailed(file))
  }

  // A failed upload no longer holds the form, and leaves the field: a single-file field says so and takes another
  // file; a multi-file one drops its tile.
  uploadFailed(file) {
    this.numberOfFilesInProgress--
    this.updateSubmitButtonStatus()
    if (this.hasFileTarget && this.myDropzone?.files.includes(file)) {
      this.element.dataset.state = 'failed'
      this.statusTarget.textContent = 'Upload failed'
    }
    this.myDropzone?.removeFile(file)
  }

  // When no explicit clickable target is given, the whole dropzone element is the drop + click
  // target (a dropzone "over the entire input"). Clicks on interactive children — the text field,
  // submit button, links — must NOT open the file picker, so we stop those from bubbling up to
  // Dropzone's click handler.
  clickableElement() {
    if (this.hasClickableTarget) return this.clickableTarget

    this.dropzoneTarget
      .querySelectorAll('input:not([type=file]), textarea, button, a, [type=submit]')
      .forEach(el => el.addEventListener('click', (event) => event.stopPropagation()))
    return this.dropzoneTarget
  }

  createDropzone() {
    return new Dropzone(this.dropzoneTarget, {
      url: this.fileFieldTarget.dataset.directUploadUrl,
      previewsContainer: this.previewTarget,
      clickable: this.clickableElement(),
      acceptedFiles: this.acceptedFilesValue,
      ...DropzoneOptions
    })
    .on('addedfile', (file) => {
      // Dropzone's own acceptedFiles check runs asynchronously *after* addedfile, so without this guard
      // an unsupported file (e.g. a .docx) would be direct-uploaded and submitted anyway. Reuse
      // Dropzone's own matcher synchronously here and skip the upload for rejected files.
      if (!Dropzone.isValidFile(file, this.acceptedFilesValue)) {
        this.myDropzone.removeFile(file)
        alert(this.rejectMessageValue)
        return
      }
      this.replaceOthers(file)
      this.showFile(file)
      const ext = file.name.substring(file.name.lastIndexOf('.') + 1)
      if (ext) {
        file.previewElement.querySelector('.dz-ext').innerHTML = ext
      }
      this.previewMedia(file)
      this.upload(file, this.fileFieldTarget)
    })
    .on('removedfile', (file) => {
      if (file.hiddenField) {
        file.hiddenField.remove()
      }
    })
  }

  connect() {
    this.fileFieldTarget.classList.add('hidden')
    this.myDropzone = this.createDropzone()
    this.updateSubmitButtonStatus()
  }

  disconnect() {
    this.myDropzone?.destroy()
    this.myDropzone = null
  }
}
